import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { PageHeader, Badge } from "@/components/Kit";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/date";
import { sortClasses } from "@/lib/classes";
import StudentForm from "./StudentForm";
import IdCard from "./IdCard";
import { Plus, Trash2, Search, Pencil, IdCard as IdIcon } from "lucide-react";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const STATUS_COLOR = { active: "#10B981", inactive: "#F59E0B", tc_issued: "#F43F5E" };

export default function Students() {
  const qc = useQueryClient();
  const { school } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [idFor, setIdFor] = useState(null);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");

  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/school/students")).data });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/students/${id}`)).data,
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["students"] }); },
  });

  const classes = sortClasses(students.map((s) => s.class_name));
  const filtered = students.filter((s) => (!cls || s.class_name === cls) && (s.name.toLowerCase().includes(q.toLowerCase()) || (s.admission_no || "").toLowerCase().includes(q.toLowerCase()) || (s.parent_phone || "").includes(q)));

  return (
    <div data-testid="students-page">
      <PageHeader
        title="Student Management"
        subtitle={`${students.length} students enrolled`}
        action={<button className="ep-btn" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-student-btn"><Plus size={16} /> New Admission</button>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="ep-card flex max-w-sm flex-1 items-center gap-2 px-3 py-2">
          <Search size={16} className="text-slate-500" />
          <input className="w-full bg-transparent text-sm text-white outline-none placeholder:text-slate-500" placeholder="Search name, admission no, mobile…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="student-search" />
        </div>
        <select className="ep-input !w-auto" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="student-class-filter">
          <option value="">All classes</option>
          {classes.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="ep-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Adm. No / PEN</th>
                <th className="px-4 py-3">Class</th>
                <th className="px-4 py-3">Father</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Fee (Total · Paid · Balance)</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const due = Math.max(0, (s.total_fee || 0) - (s.paid_fee || 0));
                return (
                  <tr key={s.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`student-row-${s.admission_no}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        {s.photo_url ? <img src={fileUrl(s.photo_url)} alt="" className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-700" /> : <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/20 text-xs font-bold text-indigo-300">{s.name.slice(0, 2).toUpperCase()}</div>}
                        <div><p className="font-medium text-white">{s.name}</p><p className="text-[11px] text-slate-500">{s.gender} · {s.caste || "—"} · DOB {fmtDate(s.dob)}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{s.admission_no}<br /><span className="text-slate-600">{s.pen || "—"}</span></td>
                    <td className="px-4 py-2.5"><Badge>{s.class_name}-{s.section}</Badge></td>
                    <td className="px-4 py-2.5 text-slate-400">{s.father_name || s.parent_name}</td>
                    <td className="px-4 py-2.5 text-slate-400">{s.parent_phone}</td>
                    <td className="px-4 py-2.5"><Badge color={STATUS_COLOR[s.status || "active"]}>{(s.status || "active").replace("_", " ")}</Badge></td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap" data-testid={`fee-cell-${s.admission_no}`}>
                      <span className="text-slate-300">{inr(s.total_fee)}</span><span className="text-slate-600"> · </span>
                      <span className="text-emerald-400">{inr(s.paid_fee)}</span><span className="text-slate-600"> · </span>
                      {due > 0 ? <span className="font-semibold text-rose-400">{inr(due)}</span> : <span className="font-semibold text-emerald-400">{s.total_fee ? "Paid" : "—"}</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => setIdFor(s)} className="rounded-lg bg-indigo-500/15 p-2 text-indigo-300 hover:bg-indigo-500/25" title="ID Card" data-testid={`idcard-${s.admission_no}`}><IdIcon size={15} /></button>
                        <button onClick={() => { setEditing(s); setOpen(true); }} className="rounded-lg bg-slate-700/40 p-2 text-slate-300 hover:bg-slate-700/70" title="Edit" data-testid={`edit-student-${s.admission_no}`}><Pencil size={15} /></button>
                        <button onClick={() => remove.mutate(s.id)} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" title="Delete" data-testid={`del-student-${s.admission_no}`}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No students found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <StudentForm open={open} onClose={() => setOpen(false)} editing={editing} />
      <IdCard student={idFor} school={school} onClose={() => setIdFor(null)} />
    </div>
  );
}
